
import { Facet, createDynamicFacet, IFacet } from '../models/Facet';
import { PolicyTipType, RetentionPolicyType } from '../models/ItemPolicy';

/**
 * Facet which provides a display name for an item.
 */
export const displayNameFacet: Facet<string> = new Facet('displayName');

/**
 * Facet which marks an item as in a deleted state.
*/
export const deletedFacet: Facet<{}> = new Facet('deleted');

/**
 * Facet which provides a pre-split file name for an item.
 */
export const fileNameFacet: Facet<{
  name: string;
  extension: string;
}> = new Facet('fileName');

/**
 * Facet which indicates that an item is a folder.
 */
export const folderFacet: Facet<{}> = new Facet('folder');

/**
 * Facet which indicates the parent folder in the file system hierarchy.
 */
export const parentFolderItemFacet: Facet<{
  itemKey: string;
}> = new Facet('parentFolderItem');

/**
 * Facet which indicates the size of a file item.
 */
export const fileSizeFacet: Facet<number> = new Facet('fileSize');

/**
 * Facet which indicates the last-modified date for a file item.
 * The value is the milliseconds for a Date object.
 */
export const dateModifiedFacet: Facet<number> = new Facet('dateModified');

/**
 * Facet which indicates who last modified a file item.
 */
export const modifiedByPersonFacet: Facet<{
  itemKey: string;
  displayValue?: string;
}> = new Facet('modifiedBy');

/**
 * Facet which indicates if this file item is Private or Shared.
 */
export const isSharedFacet: Facet<boolean> = new Facet('isShared');

/**
 * Facet which indicates the Average Rating of an item.
 */
export const averageRatingFacet: Facet<number> = new Facet('averageRating');

/**
 * Facet which indicates the how many people have rated an item.
 */
export const ratingCountFacet: Facet<number> = new Facet('ratingCount');

/**
 * Facet which indicates if this item was rated by the user in the past or not.
 */
export const hasUserRatedFacet: Facet<boolean> = new Facet('hasUserRated');

/**
 * Facet which indicates if this item rating given by the user in the past.
 */
export const userRatingFacet: Facet<number> = new Facet('userRating');

/**
 * Facet which indicates all the ratings this items has got in the past.
 */
export const allRatingsFacet: Facet<number[]> = new Facet('allRatings');

/**
 * Facet which indicates the number of likes this items has got in the past.
 */
export const likesCountFacet: Facet<number> = new Facet('likesCount');

/**
 * Facet which indicates if this item was liked by the user in the past or not.
 */
export const hasUserLikedFacet: Facet<boolean> = new Facet('hasUserLiked');

/**
 * Facet that contains the data for any user created fields on an item.
 */
export const fieldsFacet: <TValue>(id: string) => IFacet<TValue> = createDynamicFacet('fields');

/**
 * Facet which represent analytics data of the item.
 */
export const analyticsItemFacet: Facet<{
  itemKey: string;
}> = new Facet('analyticsKey');

/**
 * Facet which indicates the url to open a web-based viewer for the item.
 */
export const openUrlFacet: Facet<string> = new Facet('openUrl');

/**
 * Facet which indicates whether this is a newly created item
 */
export const isNewFacet: Facet<boolean> = new Facet('isNew');

/**
 * Facet which indicates whether the item is checked out
 * The value for byOther is the itemKey of the checked out user
 */
export const isCheckedOutFacet: Facet<{ byYou?: boolean, byOther?: string }> = new Facet('isCheckedOut');

/**
 * Facet which indicates whether malware is detected for the item
 */
export const malwareDetectedFacet: Facet<boolean> = new Facet('malwareDetected');

/**
 * Facet which indicates whether the item is missing metadata
 */
export const missingMetadataFacet: Facet<boolean> = new Facet('missingMetadata');

/**
 * Facet which indicates policy status of the item
 */
export const policyTipFacet: Facet<PolicyTipType> = new Facet('policyTip');

/**
 * Facet with indicates whether the item has retention policy applied
 */
export const retentionPolicyFacet: Facet<RetentionPolicyType> = new Facet('retentionPolicy');

/**
 * Facet with indicates whether we should show lock icon
 */
export const showRetentionPolicyIconFacet: Facet<boolean> = new Facet('showRetentionPolicyIconFacet');

/**
 * Facet for vroom url of the item
 */
export const vroomUrlFacet: Facet<string> = new Facet('vroomUrl');

/**
 * Facet for a full URL which represents the item, for identity and metadata purposes.
 */
export const canonicalUrlFacet: Facet<string> = new Facet('canonicalUrl');

/**
 * Facet for the name of the parent folder of the item.
 */
export const parentFolderItemNameFacet: Facet<string> = new Facet('parentFolderItemName');

/**
 * Facet for followed item
 */
export const followedItemFacet: Facet<{ itemKey: string }> = new Facet('followedItem');

/**
 * Facet for checking if an item is followed.
 */
export const isFollowedFacet: Facet<boolean> = new Facet('isFollowed');
